import { addDays } from 'helper/timeLabels';
import { draw } from './crmCompanies';

/**
 * The sales history of a real company, which Jobbiz does not keep yet: the
 * calls, e-mails, notes and appointments of the team, and its results. The
 * demo draws them from the company's id, so a company keeps the same history
 * from one load to the next; what the team adds then goes on top
 * (server/crm/crmApi.js keeps it per demo state).
 */

const CALL_RESULTS = ['answered', 'callback', 'no_answer', 'not_interested'];
const CALL_NOTES = [
  'Intéressé pour la saison, rappeler début novembre.',
  'A demandé la grille tarifaire, envoyée dans la foulée.',
  '',
  'Besoin de deux caristes pour décembre, à chiffrer.',
  '',
];
const EMAILS = [
  {
    direction: 'sent',
    subject: 'Proposition commerciale Jobbiz',
    body: 'Bonjour {first},\n\nComme convenu, voici notre proposition pour vos besoins en intérim de fin d’année. Les profils sont disponibles sous 48 h.\n\nBelle journée,',
  },
  {
    direction: 'sent',
    subject: 'Notre plaquette tarifaire',
    body: 'Bonjour {first},\n\nVous trouverez ci-joint notre plaquette et nos coefficients. Je reste disponible pour en parler.\n\nBien à vous,',
  },
  {
    direction: 'received',
    subject: 'Re : besoins pour décembre',
    body: 'Bonjour,\n\nNous aurons besoin de renfort sur la préparation de commandes à partir du 1er décembre. Pouvez-vous nous rappeler ?\n\nMerci,',
  },
];
const NOTES = [
  'Décideur : la gérance, joignable le mardi matin.',
  'Travaille déjà avec une autre agence pour les CDD courts.',
  'Pic d’activité en fin d’année, prévoir un point mi-novembre.',
];
const OUTCOMES = [
  'Visite du site : besoin de trois préparateurs de commandes en décembre.',
  'Présentation de Jobbiz, d’accord pour un premier test en novembre.',
  'Point sur les missions en cours, satisfait des intérimaires.',
];
const MODES = ['Sur place', 'Téléphone', 'Visio'];

const at = (now, daysAgo, hour, minutes = 0) => {
  const date = addDays(now, -daysAgo);
  date.setHours(hour, minutes, 0, 0);
  return date.toISOString();
};
const firstName = (name) => (name ?? '').split(' ')[0];

/**
 * The drawn history of a company. `contacts`: its people ({ name, email });
 * `teamIds`: the colleagues who call and write; `nextAction`: its next
 * appointment, from the companies list (crmCompanies.js), kept as is.
 */
export const companyHistory = ({ id, contacts, teamIds, now, nextAction = null }) => {
  const record = { _id: id };
  const contactOf = (salt) => (contacts.length ? contacts[draw(record, salt, contacts.length)] : null);
  const authorOf = (salt) => teamIds[draw(record, salt, teamIds.length)];

  const calls = Array.from({ length: 1 + draw(record, 'calls', 4) }, (_, index) => {
    const contact = contactOf(`call-${index}-who`);
    return {
      id: `${id}-call-${index}`,
      at: at(now, 3 + index * 9 + draw(record, `call-${index}-day`, 6), 9 + draw(record, `call-${index}-hour`, 8), 15),
      authorId: authorOf(`call-${index}-author`),
      contactName: contact?.name ?? '',
      result: CALL_RESULTS[draw(record, `call-${index}-result`, CALL_RESULTS.length)],
      note: CALL_NOTES[draw(record, `call-${index}-note`, CALL_NOTES.length)],
    };
  });

  const emails = Array.from({ length: 1 + draw(record, 'emails', 3) }, (_, index) => {
    // The models turn, so no two e-mails of a company are the same.
    const model = EMAILS[(draw(record, 'email-start', EMAILS.length) + index) % EMAILS.length];
    const contact = contactOf(`email-${index}-who`);
    const sentAt = at(now, 2 + index * 11 + draw(record, `email-${index}-day`, 5), 10 + draw(record, `email-${index}-hour`, 7), 30);
    const reads = model.direction === 'sent' ? draw(record, `email-${index}-reads`, 4) : 0;
    return {
      id: `${id}-email-${index}`,
      at: sentAt,
      direction: model.direction,
      authorId: authorOf(`email-${index}-author`),
      contactName: contact?.name ?? '',
      address: contact?.email || 'contact@entreprise.fr',
      subject: model.subject,
      body: model.body.replace('{first}', firstName(contact?.name) || 'Madame, Monsieur'),
      opens: Array.from({ length: reads }, (__, read) => addDays(new Date(sentAt), read + 1).toISOString()),
    };
  });

  const notes = Array.from({ length: draw(record, 'notes', 3) }, (_, index) => ({
    id: `${id}-note-${index}`,
    at: at(now, 4 + index * 13 + draw(record, `note-${index}-day`, 5), 14, 10),
    authorId: authorOf(`note-${index}-author`),
    body: NOTES[(draw(record, 'note-start', NOTES.length) + index) % NOTES.length],
  }));

  const events = [];
  if (draw(record, 'event-past', 3) > 0) {
    const contact = contactOf('event-past-who');
    events.push({
      id: `${id}-event-past`,
      startsAt: at(now, 6 + draw(record, 'event-past-day', 30), 9 + draw(record, 'event-past-hour', 7)),
      category: 'visite_client',
      title: '',
      contactName: contact?.name ?? '',
      mode: MODES[draw(record, 'event-past-mode', MODES.length)],
      outcome: OUTCOMES[draw(record, 'event-past-outcome', OUTCOMES.length)],
      status: 'done',
    });
  }
  if (nextAction?.kind === 'appointment') {
    const contact = contactOf('event-next-who');
    events.push({
      id: `${id}-event-next`,
      startsAt: nextAction.at,
      category: nextAction.category,
      title: '',
      contactName: contact?.name ?? '',
      mode: 'Sur place',
      outcome: '',
      status: 'planned',
    });
  }

  return { calls, emails, notes, events };
};

/**
 * The results of the sales work with a company, since its first contact:
 * the appointments held, the requests that came in, the sale and its
 * revenue, the show-up and the closing rates.
 */
export const companyResults = ({ id, history, stage, createdAt, now }) => {
  const record = { _id: id };
  const held = history.events.filter((event) => event.status === 'done').length;
  const missed = draw(record, 'no-show', 4) === 0 ? 1 : 0;
  const sales = stage === 'client' ? 1 : 0;
  const firsts = [...history.calls, ...history.emails, ...history.notes].map((item) => item.at);
  const since = [createdAt ?? now.toISOString(), ...firsts].sort()[0];
  return {
    appointmentsHeld: held,
    requests: draw(record, 'requests', 3),
    sales,
    revenue: sales ? 4000 + draw(record, 'revenue', 36) * 1000 : 0,
    showUp: held + missed ? Math.round((held / (held + missed)) * 100) : 0,
    closing: held ? Math.round((sales / held) * 100) : 0,
    since,
  };
};
